import React, { memo, ReactNode } from "react";

import { Box } from "@UI/Components/Box/Box";
import { Flex } from "@UI/Components/Flex/Flex";
import { Icon } from "@UI/Components/Icon/Icon";
import { Spinner } from "@UI/Components/Icons/Spinner";
import { Text } from "@UI/Components/Text/Text";
import { FormattedMessage } from "@I18n/Components/FormattedMessage/FormattedMessage";

import messages from "../../messages";
import { TaskCreationProgressBar } from "../TaskCreationProgressBar/TaskCreationProgressBar";

export interface ITaskCreationLayoutProps {
  /** Required questions answered vs. total required questions, excluding the
   * description screen, for the progress bar fill. Omit `totalQuestions` to
   * hide the bar. */
  answeredQuestions?: number;
  totalQuestions?: number;
  /** Current step vs. total steps, for the "STEP X OF Y" counter beside the
   * bar. Omit either to hide it. The header carries the same counter below
   * `lg`, so this one only ever shows on desktop. */
  step?: number;
  totalSteps?: number;
  /** The current step's name, shown after the counter. Omit to show the
   * counter alone. */
  stepName?: string;
  /**
   * Swaps `children` for a centred spinner. Gated here rather than in each
   * screen so a screen whose questions have not arrived shows nothing of its
   * own — no empty grid, and no Back/Continue on a screen with no question.
   */
  isLoading?: boolean;
  children: ReactNode;
}

/**
 * The body shell for a question-step screen (RT-4321): the progress bar sitting
 * at the top of the screen's own column, with the step counter to its left on
 * desktop.
 *
 * The counter is plain text, never a control: on desktop the summary panel is
 * already on screen beside the flow, so there is nothing for it to reveal. The
 * header's counter, which does open the summary, is hidden from `lg` up.
 *
 * No header here — `TaskCreationShell` mounts the shared one once at the base
 * route, above the routes, so a screen never renders its own.
 *
 * The caller passes the plain numbers it already has and never needs to know
 * how they were derived.
 */
export const TaskCreationLayout = memo(
  ({
    answeredQuestions,
    totalQuestions,
    step,
    totalSteps,
    stepName,
    isLoading = false,
    children,
  }: ITaskCreationLayoutProps) => {
    return (
      // No horizontal padding: `TaskCreationShell` owns the page gutter now, so
      // adding one here would inset the bar inside the column twice.
      <Box>
        <Flex paddingTop="16px" alignItems="center" gap="16px">
          {Boolean(step && totalSteps) && (
            <Text
              as="span"
              display={{ base: "none", lg: "block" }}
              flexShrink={0}
              fontWeight="700"
              fontSize="sm"
              lineHeight="1.5"
              color="gray.800"
              textTransform="uppercase"
              whiteSpace="nowrap"
            >
              <FormattedMessage
                {...messages["taskCreation.header.stepIndicator"]}
                values={{ step, total: totalSteps }}
              />
              {stepName && (
                // Not uppercased with the counter: the step's own name is title
                // case here and in the summary panel beside it.
                <Text as="span" textTransform="none">
                  {` · ${stepName}`}
                </Text>
              )}
            </Text>
          )}
          <Box flex="1" minWidth={0}>
            <TaskCreationProgressBar
              completed={answeredQuestions}
              total={totalQuestions}
            />
          </Box>
        </Flex>
        {isLoading ? (
          <Flex justifyContent="center" alignItems="center" minHeight="320px">
            {/* Decorative: the spinner carries no accessible name, matching
                `LoadingScreen`. Announcing the wait needs a labelled live
                region, not an `aria-busy` container nothing points at. */}
            <Icon as={Spinner} boxSize="32px" color="gray.400" />
          </Flex>
        ) : (
          children
        )}
      </Box>
    );
  }
);

TaskCreationLayout.displayName = "TaskCreationLayout";
